import React, { useEffect, useState } from 'react';
import { Popover } from '@mantine/core';

import { showToast } from 'utils/utility';
import { getRole } from 'utils/routeUtility';
import { useApi, useHotelData } from 'libs/data-access';
import { IHotelOnlineBookingList } from 'libs/data-access/types';
import { EButton, ECheckbox, EPopover, ETextInput } from 'libs/ui';

interface IProps {
    label: string;
    hotelTicket: IHotelOnlineBookingList;
    handleRefresh: () => void;
};

export default function MarkAsToBeRefunded({ ...props }: IProps) {

    const {
        getData
    } = useApi();
    const {
        postHotelData
    } = useHotelData();

    const [cancellationCharges, setCancellationCharges] = useState<{
        affiliateDomesticCharge: number;
        affiliateInternationalCharge: number;
        customerDomesticCharge: number;
        customerInternationalCharge: number;
    }>({
        affiliateDomesticCharge: 0,
        affiliateInternationalCharge: 0,
        customerDomesticCharge: 0,
        customerInternationalCharge: 0
    });
    const [rejectTicketState, setRejectTicketState] = useState<{
        opened: boolean;
        supplierCharge: string | null;
        cancellationCharge: string | null;
        confirmed: boolean;
        isSaving: boolean;
    }>({
        opened: false,
        supplierCharge: null,
        cancellationCharge: null,
        confirmed: false,
        isSaving: false
    });


    useEffect(() => {
        getCancellationCharges();
    }, []);


    // HANDLER FUNCTIONS
    const closePopover = () => setRejectTicketState({
        opened: false,
        supplierCharge: null,
        cancellationCharge: null,
        confirmed: false,
        isSaving: false
    });


    // API CALLS
    const getCancellationCharges = async () => {
        const response = await getData(`my-charges/hotels/cancellation-charges?userType=${getRole()?.[0]}`);

        if (response.isSuccess) {
            // console.log("Response while fetching cancellation charges: ", response.data.data);
            setCancellationCharges(response.data.data);
            setRejectTicketState({ ...rejectTicketState, cancellationCharge: response.data.data.customerDomesticCharge.toString() });
        } else {
            console.log("Error while fetching cancellation charges: ", response);
            if (response.status !== 404) {
                showToast({
                    type: "error",
                    message: response.message || "Error while fetching cancellation charges",
                });
            };
        };
    };

    const resolveTicket = async () => {

        if (!props.hotelTicket?.id) {
            showToast({
                time: 5 * 1000,
                type: "warning",
                message: "Please select a booking and provide the ticket information first!"
            });
            return;
        };

        if (!rejectTicketState.supplierCharge || !rejectTicketState.cancellationCharge) {
            showToast({
                type: "warning",
                message: "Please fill the charges!"
            });
            return;
        };

        // console.log("rejectTicketState: ", rejectTicketState);

        setRejectTicketState({ ...rejectTicketState, isSaving: true });

        const response = await postHotelData(`/booking/${props.hotelTicket.id}/cancellation-charges?supplierCharge=${rejectTicketState.supplierCharge}&cancellationCharge=${rejectTicketState.cancellationCharge}`, null);

        if (response.isSuccess) {
            // console.log("Response for resolving ticket: ", response);
            showToast({
                type: "success",
                message: "Ticket rejected successfully!"
            });
            closePopover();
            props.handleRefresh();
        } else {
            console.log("Error while resolving ticket: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while resolving ticket!"
            });
        };

        setRejectTicketState({ ...rejectTicketState, isSaving: false });

    };


    return (
        <EPopover
            width={450}
            opened={rejectTicketState.opened}
            onClose={closePopover}
        >
            <Popover.Target>
                <div>
                    <EButton
                        size="sm"
                        variant="light"
                        color="red"
                        disabled={props.hotelTicket?.bookingStatus === "SUCCESS" || props.hotelTicket?.bookingStatus === "REFUNDED"}
                        onClick={() => setRejectTicketState({ ...rejectTicketState, opened: !rejectTicketState.opened })}
                    >{props.label}</EButton>
                </div>
            </Popover.Target>

            <Popover.Dropdown>
                <div className="flex flex-col gap-6">
                    {
                        props.hotelTicket?.id
                            ?
                            <>
                                <ETextInput
                                    label="Supplier Charge"
                                    placeholder="Enter supplier charge"
                                    value={rejectTicketState.supplierCharge ?? undefined}
                                    onChange={(e) => setRejectTicketState({ ...rejectTicketState, supplierCharge: e.target.value })}
                                />
                                <ETextInput
                                    label="My Cancellation Charge"
                                    placeholder="Enter my cancellation charge"
                                    value={rejectTicketState.cancellationCharge ?? undefined}
                                    onChange={(e) => setRejectTicketState({ ...rejectTicketState, cancellationCharge: e.target.value })}
                                />

                                <ECheckbox
                                    // indeterminate
                                    label="Are you sure you want to resolve this ticket?"
                                    checked={rejectTicketState.confirmed}
                                    onChange={(e) => setRejectTicketState({ ...rejectTicketState, confirmed: e.target.checked })}
                                />

                                {
                                    rejectTicketState.confirmed
                                        ?
                                        <div className="flex justify-end gap-2">
                                            <EButton
                                                variant="light"
                                                onClick={closePopover}
                                            >Cancel</EButton>
                                            <EButton
                                                onClick={resolveTicket}
                                            >Save</EButton>
                                        </div>
                                        :
                                        <></>
                                }
                            </>
                            :
                            <></>
                    }
                </div>
            </Popover.Dropdown>
        </EPopover>
    )
}